import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  useMediaQuery,
  useTheme,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { motion } from "framer-motion";
import { Typewriter } from "react-simple-typewriter";
import { MuiTelInput } from "mui-tel-input";
import { useTranslation } from "react-i18next";
import { modalStyles } from "../../../theme/modalStyles";
import { useDashboardTheme } from "../context/DashboardThemeContext";

export const PhoneModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onConfirm: (phone: string) => void;
}> = ({ open, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const isDark = useDashboardTheme();
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState(() => {
    const browserLang = navigator.language.split("-")[1]?.toUpperCase();
    return browserLang === "BR" ? "BR" : "US";
  });
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && phone.trim()) onConfirm(phone);
  };

  const handlePhoneChange = (newPhone: string) => {
    setPhone(newPhone);

    switch (true) {
      case newPhone.startsWith("+55"):
        setCountry("BR");
        break;
      case newPhone.startsWith("+1"):
        setCountry("US");
        break;
      case newPhone.startsWith("+351"):
        setCountry("PT");
        break;
      case newPhone.startsWith("+34"):
        setCountry("ES");
        break;
      case newPhone.startsWith("+54"):
        setCountry("AR");
        break;
      default:
        break;
    }
  };

  const getPlaceholder = () => {
    switch (country) {
      case "BR":
        return "(11) 99999-9999";
      case "US":
        return "(555) 123-4567";
      case "PT":
        return "912 345 678";
      case "ES":
        return "612 34 56 78";
      case "AR":
        return "11 1234-5678";
      default:
        return "+1 555-555-5555";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="xs"
      fullWidth
      BackdropProps={{ style: { backdropFilter: "blur(4px)" } }}
      PaperProps={{
        style: modalStyles.paper,
        sx: isDark
          ? {
              bgcolor: "rgba(20, 20, 20, 0.98)",
              border: "1px solid rgba(255,255,255,0.08)",
            }
          : undefined,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Box
          sx={{
            ...modalStyles.header(theme),
            background: isDark
              ? "linear-gradient(90deg, #2d1b69 0%, #1a3a4a 100%)"
              : "linear-gradient(90deg, #93F090 0%, #60BFB7 100%)",
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8, color: "#fff" }}
          >
            <CloseIcon />
          </IconButton>
          <Box textAlign="center">
            <Typewriter
              words={[t("whats_your_company_number")]}
              cursor
              cursorStyle="|"
              typeSpeed={60}
              deleteSpeed={50}
              delaySpeed={1000}
            />
          </Box>
        </Box>
      </motion.div>

      <DialogContent sx={modalStyles.content}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <MuiTelInput
            value={phone}
            onChange={handlePhoneChange}
            onKeyDown={handleKeyDown}
            defaultCountry={country as any}
            preferredCountries={["BR", "US", "PT", "ES", "AR"]}
            fullWidth
            size="small"
            margin="dense"
            autoFocus
            placeholder={getPlaceholder()}
            forceCallingCode
            focusOnSelectCountry
            sx={
              isDark
                ? {
                    "& .MuiInputLabel-root": { color: "#a0a0a0" },
                    "& .MuiInputBase-input": { color: "#ffffff" },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.2)",
                    },
                    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                      { borderColor: "rgba(255,255,255,0.4)" },
                    "& .MuiButtonBase-root": { color: "#e0e0e0" },
                  }
                : undefined
            }
          />
        </motion.div>
      </DialogContent>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.3 }}
      >
        <DialogActions
          sx={{
            ...modalStyles.actions,
            borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : undefined,
          }}
        >
          <Button
            onClick={onClose}
            sx={{ color: isDark ? "#a0a0a0" : undefined }}
          >
            {t("Cancel")}
          </Button>
          <Button
            onClick={() => onConfirm(phone)}
            variant="contained"
            disabled={!phone.trim()}
          >
            {t("phone_modal_next")}
          </Button>
        </DialogActions>
      </motion.div>
    </Dialog>
  );
};
